/* eslint-disable react-refresh/only-export-components */
import React, { useState, useEffect, useContext, useCallback } from "react";
import { supabase } from "../lib/supabase";
import toast from "react-hot-toast";
import {
  useProductsQuery,
  useProductMutations,
} from "../hooks/useProductsQuery";

import { ProductContext } from "./Contexts";

export { ProductContext };

export function useProducts() {
  const context = useContext(ProductContext);
  if (context === undefined || context === null) {
    throw new Error("useProducts must be used within a ProductProvider");
  }
  return context;
}

export function ProductProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(7.0);
  const [loading, setLoading] = useState(true);
  const [currentCurrency, setCurrentCurrency] = useState("BOB");
  const [suppliers, setSuppliers] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [newsTickerText, setNewsTickerText] = useState("");

  async function logAnalyticsEvent(eventType, eventData = {}) {
    try {
      await supabase.from("analytics_logs").insert([
        {
          event_type: eventType,
          event_data: eventData,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch {
      console.log("Analytics Log skipped (Table may not exist):", eventType);
    }
  }

  const fetchSuppliers = useCallback(async function () {
    try {
      const { data, error } = await supabase
        .from("suppliers")
        .select("*")
        .order("name");
      if (error) throw error;
      setSuppliers(data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  }, []);

  async function addSupplier(name, color) {
    try {
      const { data, error: insertError } = await supabase
        .from("suppliers")
        .insert([{ name, color }])
        .select();
      if (insertError) throw insertError;
      setSuppliers((prev) => [...prev, data[0]]);
      toast.success("Proveedor agregado");
      return true;
    } catch (error) {
      console.error("Error al agregar proveedor:", error);
      toast.error(
        `Error al agregar proveedor: ${error.message || "Error desconocido"}`,
      );
      return false;
    }
  }

  async function updateSupplier(id, name, color) {
    try {
      const { data, error: updateError } = await supabase
        .from("suppliers")
        .update({ name, color })
        .eq("id", id)
        .select();
      if (updateError) throw updateError;
      setSuppliers((prev) => prev.map((s) => (s.id === id ? data[0] : s)));
      toast.success("Proveedor actualizado");
      return true;
    } catch (error) {
      console.error("Error al actualizar proveedor:", error);
      toast.error(
        `Error al actualizar proveedor: ${error.message || "Error desconocido"}`,
      );
      return false;
    }
  }

  async function deleteSupplier(id) {
    try {
      const { error: deleteError, count } = await supabase
        .from("suppliers")
        .delete({ count: "exact" })
        .eq("id", id);
      if (deleteError || count === 0) {
        throw new Error(
          deleteError?.message ||
            "No se pudo eliminar el proveedor. Verifique los permisos (RLS).",
        );
      }
      setSuppliers((prev) => prev.filter((s) => s.id !== id));
      toast.success("Proveedor eliminado");
      return true;
    } catch (error) {
      toast.error(
        `Error al eliminar proveedor: ${error?.message || "Error desconocido"}`,
      );
      return false;
    }
  }

  function getSupplierColor(supplierName) {
    const s = suppliers.find(
      (sup) => sup.name.toLowerCase() === (supplierName || "").toLowerCase(),
    );
    return s ? s.color : "#525252";
  }

  function toggleCurrency() {
    setCurrentCurrency((prev) => (prev === "BOB" ? "USD" : "BOB"));
  }

  function formatPrice(priceInBob) {
    if (currentCurrency === "BOB") {
      return new Intl.NumberFormat("es-BO", {
        style: "currency",
        currency: "BOB",
      }).format(priceInBob);
    } else {
      const priceInUsd = priceInBob / exchangeRate;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(priceInUsd);
    }
  }

  // React Query Integration
  const {
    data: productsData,
    isLoading: productsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useProductsQuery();

  const {
    addProduct: addProductMutation,
    updateProduct: updateProductMutation,
    deleteProduct: deleteProductMutation,
  } = useProductMutations();

  // Sync React Query data to local state context API expects
  useEffect(() => {
    if (productsData) {
      setProducts(productsData);
      if (productsData.length === 0) {
        console.warn(
          "ProductContext: Se cargaron 0 productos. Verifique si la base de datos está vacía o si el proyecto Supabase está pausado.",
        );
      }
    }
  }, [productsData]);

  // Combined loading state
  useEffect(() => {
    setLoading(productsLoading);
  }, [productsLoading]);

  // Manejo de errores de conexión
  useEffect(() => {
    if (productsError) {
      console.error("Error loading products:", productsError);

      let errorMessage =
        "Error de conexión: No se pudieron cargar los productos.";

      // Detect specific network errors
      const errorStr = productsError.toString().toLowerCase();
      if (
        errorStr.includes("fetch failed") ||
        errorStr.includes("enotfound") ||
        errorStr.includes("network error")
      ) {
        errorMessage =
          "Error de Red: No se puede conectar con el servidor de Supabase. Verifique su conexión a internet o si la URL en .env es correcta.";
      } else if (
        errorStr.includes("401") ||
        errorStr.includes("unauthorized")
      ) {
        errorMessage =
          "Error de Autenticación: Verifique su VITE_SUPABASE_ANON_KEY en el archivo .env.";
      } else if (errorStr.includes("api key not found")) {
        errorMessage =
          "Error de Configuración: La API Key de Supabase no es válida o falta.";
      }

      toast.error(
        `${errorMessage} Verifique si el proyecto Supabase está activo (no pausado).`,
        { duration: 8000, id: "supabase-connection-error" },
      );
    }
  }, [productsError]);

  const fetchCategories = useCallback(async function () {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      if (data) setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error.message);
    }
  }, []);

  async function addCategory(name) {
    try {
      const { data, error } = await supabase
        .from("categories")
        .insert([{ name }])
        .select();

      if (error) throw error;
      if (data) {
        setCategories(
          [...categories, data[0]].sort((a, b) => a.name.localeCompare(b.name)),
        );
        toast.success("Categoría agregada");
        return true;
      }
    } catch (error) {
      console.error("Error adding category:", error);
      toast.error("Error al agregar categoría");
      return false;
    }
  }

  async function updateCategory(id, name) {
    try {
      const { data, error } = await supabase
        .from("categories")
        .update({ name })
        .eq("id", id)
        .select();

      if (error) throw error;
      if (data) {
        setCategories(
          categories
            .map((cat) => (cat.id === id ? data[0] : cat))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
        toast.success("Categoría actualizada");
        return true;
      }
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error("Error al actualizar categoría");
      return false;
    }
  }

  async function deleteCategory(id) {
    try {
      // Step 1: Find the category name from the local state
      const categoryToDelete = categories.find((c) => c.id == id);
      if (!categoryToDelete) {
        toast.error("Categoría no encontrada.");
        return false;
      }

      // Step 2: Check for usage directly in the database
      const { count: productCount, error: productError } = await supabase
        .from("products")
        .select("id", { count: "exact", head: true })
        .eq("category", categoryToDelete.name);

      if (productError) {
        throw new Error(`Error al verificar uso: ${productError.message}`);
      }

      if (productCount && productCount > 0) {
        toast.error(
          `No se puede eliminar: La categoría está en uso por al menos ${productCount} producto(s).`,
        );
        return false;
      }

      // Step 3: If not in use, proceed with deletion and check the count
      console.log("Attempting to delete category with ID:", id);
      const { error: deleteError, count } = await supabase
        .from("categories")
        .delete({ count: "exact" })
        .eq("id", id);
      console.log(
        "Supabase deletion result - deleteError:",
        deleteError,
        "count:",
        count,
      );

      // Throw an error if deletion fails or if no rows were deleted (silent RLS fail)
      if (deleteError || count === 0) {
        throw new Error(
          deleteError?.message ||
            "No se pudo eliminar la categoría. Verifique los permisos (RLS).",
        );
      }

      // Step 4: Update local state only after successful deletion
      setCategories((prev) => prev.filter((cat) => cat.id !== id));
      toast.success("Categoría eliminada con éxito");
      return true;
    } catch (error) {
      console.error("Error al eliminar la categoría:", error);
      toast.error(`Error al eliminar: ${error.message}`);
      return false;
    }
  }

  async function recordVisit() {
    // Evitar duplicados por recarga o si la tabla no existe
    if (
      sessionStorage.getItem("neotec_visit_recorded") ||
      sessionStorage.getItem("supabase_table_daily_stats_missing")
    ) {
      return;
    }

    try {
      // Log generic visit event
      logAnalyticsEvent("page_visit", {
        path: window.location.pathname,
        referrer: document.referrer,
      });

      const today = new Date().toISOString().split("T")[0];

      // Verificar si ya existe registro para hoy
      const { data } = await supabase
        .from("daily_stats")
        .select("id, visits")
        .eq("date", today)
        .limit(1);

      const existing = data && data.length > 0 ? data[0] : null;

      if (existing) {
        await supabase
          .from("daily_stats")
          .update({ visits: (existing.visits || 0) + 1 })
          .eq("id", existing.id);
      } else {
        await supabase.from("daily_stats").insert([{ date: today, visits: 1 }]);
      }

      // Marcar visita como registrada en esta sesión
      sessionStorage.setItem("neotec_visit_recorded", "true");
    } catch (error) {
      // Si la tabla no existe, marcamos en la sesión para no volver a intentar
      if (
        error.code === "PGRST205" ||
        (error.message && error.message.includes("not found"))
      ) {
        sessionStorage.setItem("supabase_table_daily_stats_missing", "true");
        console.warn(
          "Analytics table missing. Stopping background attempts for this session.",
        );
      } else {
        console.error("Error recording visit:", error);
      }
    }
  }

  const fetchDailyStats = useCallback(async function () {
    try {
      const { data, error } = await supabase
        .from("daily_stats")
        .select("*")
        .order("date", { ascending: false })
        .limit(30); // Increased limit for better charts

      if (error) throw error;
      if (data) setDailyStats([...data].reverse());
    } catch (error) {
      // Si la tabla no existe, marcamos para no reintentar
      if (
        error.code === "PGRST205" ||
        (error.message && error.message.includes("not found"))
      ) {
        sessionStorage.setItem("supabase_table_daily_stats_missing", "true");
      } else {
        console.error("Error fetching daily stats:", error);
      }
    }
  }, []);

  const [sales, setSales] = useState([]);

  const fetchSales = useCallback(async function () {
    try {
      const { data, error } = await supabase
        .from("sales")
        .select("*")
        .order("sale_date", { ascending: false });
      if (error) throw error;
      if (data) setSales(data);
    } catch (error) {
      console.error("Error fetching sales:", error);
    }
  }, []);

  async function addSale(saleData) {
    try {
      const { data, error } = await supabase
        .from("sales")
        .insert([saleData])
        .select();

      if (error) throw error;
      if (data) setSales([data[0], ...sales]);
      toast.success("Venta registrada correctamente");
      return true;
    } catch (error) {
      console.error("Error registering sale:", error);
      toast.error("Error al registrar la venta");
      return false;
    }
  }

  const fetchExchangeRate = useCallback(async function () {
    try {
      const { data, error } = await supabase
        .from("settings")
        .select("value")
        .eq("key", "exchange_rate_usd_bob")
        .single();

      if (error) throw error;
      if (data) setExchangeRate(parseFloat(data.value));
    } catch (error) {
      console.error("Error fetching exchange rate:", error.message);
    }
  }, []);

  async function updateExchangeRate(newRate) {
    try {
      // Update the exchange rate in settings
      const { error: settingsError } = await supabase
        .from("settings")
        .update({
          value: newRate.toString(),
          updated_at: new Date().toISOString(),
        })
        .eq("key", "exchange_rate_usd_bob");

      if (settingsError) throw settingsError;

      // Recalculate prices for all products that were added in USD
      const { data: usdProducts, error: fetchError } = await supabase
        .from("products")
        .select("id, original_price, currency")
        .eq("currency", "USD");

      if (fetchError) throw fetchError;

      if (usdProducts && usdProducts.length > 0) {
        // Update each USD product with the new exchange rate
        const updates = usdProducts.map((product) => ({
          id: product.id,
          price: product.original_price * newRate,
        }));

        const updatePromises = updates.map((update) =>
          supabase
            .from("products")
            .update({ price: update.price })
            .eq("id", update.id),
        );
        await Promise.all(updatePromises);

        toast.success(
          `Tipo de cambio actualizado. ${usdProducts.length} producto(s) recalculado(s).`,
        );
        toast.success("Tipo de cambio actualizado");
      }

      setExchangeRate(newRate);
      // Refresh products to show updated prices
      fetchProducts();
    } catch (error) {
      console.error("Error updating exchange rate:", error.message);
      toast.error("Error al actualizar el tipo de cambio");
    }
  }

  // --- BANNER MANAGEMENT ---
  const [bannerUrls, setBannerUrls] = useState(["/banner.jpg"]); // Default list

  const fetchBanner = useCallback(async function () {
    try {
      const { data, error } = await supabase
        .from("settings")
        .select("value")
        .eq("key", "home_banner_url")
        .single();

      if (data && data.value) {
        try {
          // Attempt to parse as JSON (array)
          const parsed = JSON.parse(data.value);
          if (Array.isArray(parsed)) {
            setBannerUrls(parsed.length > 0 ? parsed : ["/banner.jpg"]);
          } else {
            // Legacy single string
            setBannerUrls([data.value]);
          }
        } catch {
          // Failure to parse means it's a legacy single string
          setBannerUrls([data.value]);
        }
      } else if (error && error.code !== "PGRST116") {
        // PGRST116 is single() not found, which is fine
        throw error;
      }
    } catch (error) {
      console.error("Error fetching banner:", error.message);
    }
  }, []);

  async function addBanner(file) {
    try {
      const url = await uploadImage(file);
      if (!url) throw new Error("No se pudo obtener la URL de la imagen");

      const newUrls = [...bannerUrls.filter((u) => u !== "/banner.jpg"), url];

      const { error } = await supabase.from("settings").upsert(
        {
          key: "home_banner_url",
          value: JSON.stringify(newUrls),
          updated_at: new Date().toISOString(),
        },
        { onConflict: "key" },
      );

      if (error) throw error;

      setBannerUrls(newUrls);
      return true;
    } catch (error) {
      console.error("Error adding banner:", error);
      throw error;
    }
  }

  async function deleteBanner(urlToDelete) {
    try {
      const newUrls = bannerUrls.filter((url) => url !== urlToDelete);
      const finalUrls = newUrls.length > 0 ? newUrls : ["/banner.jpg"];

      const { error } = await supabase.from("settings").upsert(
        {
          key: "home_banner_url",
          value: JSON.stringify(finalUrls),
          updated_at: new Date().toISOString(),
        },
        { onConflict: "key" },
      );

      if (error) throw error;

      setBannerUrls(finalUrls);
      toast.success("Banner eliminado");
      return true;
    } catch (error) {
      console.error("Error deleting banner:", error);
      toast.error("Error al eliminar banner");
      return false;
    }
  }

  // --- NEWS TICKER MANAGEMENT ---
  const fetchNewsTickerText = useCallback(async function () {
    try {
      const { data } = await supabase
        .from("settings")
        .select("value")
        .eq("key", "news_ticker_text")
        .single();

      if (data && data.value) {
        setNewsTickerText(data.value);
      }
    } catch (error) {
      console.error("Error fetching news ticker text:", error.message);
    }
  }, []);

  async function updateNewsTickerText(text) {
    try {
      const { error } = await supabase.from("settings").upsert(
        {
          key: "news_ticker_text",
          value: text,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "key" },
      );

      if (error) throw error;

      setNewsTickerText(text);
      toast.success("Live Update actualizado");
      return true;
    } catch {
      console.error("Error updating news ticker text");
      toast.error("Error al actualizar Live Update");
      return false;
    }
  }

  async function fetchProducts() {
    return refetchProducts();
  }

  async function getPaginatedProducts({
    page = 1,
    limit = 12,
    searchTerm = "",
    category = "Todos",
    minPrice = "",
    maxPrice = "",
    sortBy = "newest",
    brand = "Todas",
  }) {
    try {
      let query = supabase.from("products").select("*", { count: "exact" });

      // Search
      if (searchTerm) {
        query = query.or(
          `name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`,
        );
      }

      // Category
      if (category !== "Todos") {
        query = query.eq("category", category);
      }

      // Brand (Simple implementation assuming brand is in name)
      if (brand !== "Todas") {
        query = query.ilike("name", `%${brand}%`);
      }

      // Price filters
      if (minPrice) query = query.gte("price", parseFloat(minPrice));
      if (maxPrice) query = query.lte("price", parseFloat(maxPrice));

      // Sorting
      switch (sortBy) {
        case "price-asc":
          query = query.order("price", { ascending: true });
          break;
        case "price-desc":
          query = query.order("price", { ascending: false });
          break;
        case "name-asc":
          query = query.order("name", { ascending: true });
          break;
        case "name-desc":
          query = query.order("name", { ascending: false });
          break;
        case "popularity":
          query = query.order("views", { ascending: false, nullsFirst: false });
          break;
        case "stock-asc":
          query = query.order("stock", { ascending: true });
          break;
        case "stock-desc":
          query = query.order("stock", { ascending: false });
          break;
        case "newest":
        default:
          query = query.order("created_at", { ascending: false });
          break;
      }

      // Pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;
      return { data, count };
    } catch {
      console.error("Error fetching paginated products");
      return { data: [], count: 0 };
    }
  }

  async function addProduct(product) {
    try {
      await addProductMutation.mutateAsync(product);
      return true;
    } catch {
      return false;
    }
  }

  async function updateProduct(id, updatedProduct) {
    try {
      await updateProductMutation.mutateAsync({ id, ...updatedProduct });
      return true;
    } catch {
      return false;
    }
  }

  async function bulkAddProducts(productsArray, syncStock = false) {
    try {
      const toInsert = [];
      const toUpdate = [];
      let updatedCount = 0;
      let insertedCount = 0;
      let syncedCount = 0;

      // Classify products into Insert or Update
      productsArray.forEach((csvProduct) => {
        const existingProduct = products.find(
          (p) =>
            p.name.toLowerCase().trim() ===
            csvProduct.name.toLowerCase().trim(),
        );

        const productWithSupplier = {
          ...csvProduct,
          supplier: csvProduct.supplier || "General",
        };

        if (existingProduct) {
          // Prepare for update
          toUpdate.push({
            id: existingProduct.id,
            ...productWithSupplier,
          });
        } else {
          toInsert.push(productWithSupplier);
        }
      });

      // Execute Inserts
      if (toInsert.length > 0) {
        const { error: insertError } = await supabase
          .from("products")
          .insert(toInsert);

        if (insertError) throw insertError;
        insertedCount = toInsert.length;
      }

      // Execute Updates
      if (toUpdate.length > 0) {
        const updatePromises = toUpdate.map(async (product) => {
          const { id, ...updateData } = product;
          const { error: updateError } = await supabase
            .from("products")
            .update(updateData)
            .eq("id", id);

          if (updateError) {
            console.error(
              `Error updating product ${product.name}:`,
              updateError,
            );
            return false;
          }
          return true;
        });

        const results = await Promise.all(updatePromises);
        updatedCount = results.filter(Boolean).length;
      }

      // Sync stock if requested: Set stock to 0 for products NOT in the CSV list
      if (syncStock) {
        const namesInCsv = new Set(
          productsArray.map((p) => p.name.toLowerCase().trim()),
        );
        const toMarkOutOfStock = products.filter(
          (p) => !namesInCsv.has(p.name.toLowerCase().trim()),
        );

        if (toMarkOutOfStock.length > 0) {
          const syncPromises = toMarkOutOfStock.map(async (p) => {
            const { error } = await supabase
              .from("products")
              .update({ stock: 0 })
              .eq("id", p.id);
            return !error;
          });
          const results = await Promise.all(syncPromises);
          syncedCount = results.filter(Boolean).length;
        }
      }

      // Refresh local state
      await fetchProducts();

      let message = `Proceso completado: ${insertedCount} agregados, ${updatedCount} actualizados`;
      if (syncStock) {
        message += `, ${syncedCount} puestos en stock agotado`;
      }
      toast.success(message);

      return {
        success: true,
        count: insertedCount + updatedCount + syncedCount,
      };
    } catch (error) {
      console.error("Error processing bulk products:", error.message);
      toast.error("Error al procesar productos");
      return { success: false, error: error.message };
    }
  }

  async function deleteProduct(id) {
    try {
      await deleteProductMutation.mutateAsync(id);
      return true;
    } catch {
      return false;
    }
  }

  async function uploadImage(file) {
    try {
      const fileExt = file.name.split(".").pop();
      // Use timestamp + random string to avoid collisions and caching issues
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("products")
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage.from("products").getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error("Error uploading image:", error.message);
      toast.error(`Error al subir imagen: ${error.message}`);
      throw error; // Propagate error
    }
  }

  async function recordProductView(productId, productName) {
    try {
      // 1. Contador persistente en tabla products
      const { error } = await supabase.rpc("increment_product_views", {
        product_id: productId,
      });

      if (error) {
        const { data: p } = await supabase
          .from("products")
          .select("views")
          .eq("id", productId)
          .single();
        if (p) {
          await supabase
            .from("products")
            .update({ views: (p.views || 0) + 1 })
            .eq("id", productId);
        }
      }

      // 2. Evento detallado
      logAnalyticsEvent("product_view", { id: productId, name: productName });
    } catch (error) {
      console.error("Error al registrar vista de producto:", error);
    }
  }

  async function recordSearch(term, resultCount) {
    if (!term || term.trim().length < 2) return;
    try {
      if (resultCount === 0) {
        logAnalyticsEvent("failed_search", { term });
      } else {
        logAnalyticsEvent("search", { term, results: resultCount });
      }
    } catch (error) {
      console.error("Error al registrar búsqueda:", error);
    }
  }

  async function recordCartInteraction(productId, productName, action = "add") {
    logAnalyticsEvent("cart_action", {
      id: productId,
      name: productName,
      action,
    });
  }

  async function recordWishlistInteraction(
    productId,
    productName,
    action = "add",
  ) {
    logAnalyticsEvent("wishlist_action", {
      id: productId,
      name: productName,
      action,
    });
  }

  async function recordWhatsAppClick(cartItems) {
    logAnalyticsEvent("whatsapp_order_click", {
      itemCount: cartItems.length,
      items: cartItems.map((i) => ({ id: i.id, name: i.name })),
    });
  }

  async function fetchAnalytics() {
    try {
      const { data, error } = await supabase
        .from("analytics_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1000);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching analytics:", error);
      return [];
    }
  }
  useEffect(() => {
    // React Query handle fetchProducts independently, but we call these for initial state
    fetchSuppliers();
    fetchCategories();
    fetchExchangeRate();
    fetchSales();
    fetchBanner();
    fetchNewsTickerText();

    if (!sessionStorage.getItem("supabase_table_daily_stats_missing")) {
      fetchDailyStats();
    }
  }, [
    fetchSuppliers,
    fetchCategories,
    fetchExchangeRate,
    fetchSales,
    fetchBanner,
    fetchNewsTickerText,
    fetchDailyStats,
  ]);

  return (
    <ProductContext.Provider
      value={{
        products,
        categories,
        exchangeRate,
        loading,
        currentCurrency,
        toggleCurrency,
        formatPrice,
        addProduct,
        bulkAddProducts,
        updateProduct,
        deleteProduct,
        uploadImage,
        recordProductView,
        recordSearch,
        recordCartInteraction,
        recordWishlistInteraction,
        recordWhatsAppClick,
        fetchAnalytics,

        // Banner
        bannerUrls,
        addBanner,
        deleteBanner,

        // News Ticker
        newsTickerText,
        updateNewsTickerText,

        updateExchangeRate,
        fetchProducts, // Exposed for Admin
        getPaginatedProducts, // Exposed for Home
        getSupplierColor, // Exposed for UI
        suppliers,
        addSupplier,
        updateSupplier,
        deleteSupplier,
        refetchProducts, // Exposed for manual refresh if needed
        fetchSuppliers,
        addCategory,
        updateCategory,
        deleteCategory,
        dailyStats,
        recordVisit,
        addSale,
        sales,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
}

export default ProductProvider;
